import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { RealtimeProvider } from '../../hooks/useRealtime.tsx'
import { useSession } from '../../hooks/useSession.ts'
import { useUsers } from '../../hooks/useTasks.ts'
import { setKnownMentionHandles } from '../../lib/markdown.ts'
import { TopBar } from './TopBar.tsx'
import styles from './AppShell.module.css'

/**
 * Guarded shell: everything except /login renders inside it. While the session query is in flight
 * we render nothing rather than flashing the login screen at someone who is already logged in.
 */
export function AppShell(): React.JSX.Element | null {
  const session = useSession()
  const location = useLocation()
  const users = useUsers()

  // Rendering markdown is synchronous and happens deep in the tree, so the list of real people is
  // handed to the renderer once here rather than threaded through every component that renders text.
  useEffect(() => {
    setKnownMentionHandles(
      (users.data ?? []).flatMap((u) => [
        u.displayName.toLowerCase().replace(/\s+/g, ''),
        u.email.split('@')[0]?.toLowerCase() ?? '',
      ]),
    )
  }, [users.data])

  if (session.isPending) return null
  if (!session.data) return <Navigate to="/login" replace state={{ from: location.pathname }} />

  return (
    <RealtimeProvider meId={session.data.id}>
      <div className={styles.shell}>
        {/* Skip link: the top bar is a dozen tab stops that repeat on every route, so without it
            keyboard users walk the whole navigation before reaching the thing they came for. */}
        <a href="#main" className={styles.skipLink}>
          Przejdź do treści
        </a>
        <TopBar user={session.data} />
        <main id="main" className={styles.content} tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </RealtimeProvider>
  )
}
